import { AxiosResponse } from "axios";
import { useContext } from "react";
import {
  Attachments,
  PartMappingRequest,
  PartsMappingResponse,
  PartsMappingResponseV2,
  Promotions,
  PromotionType,
} from "./types";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import { ConfigContext } from "../../context/ConfigContext";
import { PromotionDownloadStatusesResponse } from "../../hooks/usePromotionDownloadStatus/usePromotionDownloadStatus";
import { PromotionsDetails } from "../../pages/promotionsV2/PromotionDetails/type";
import { Attachment, IPromotion } from "../../types";
import { BlobResponse } from "../../utils/downloadFile";
import { PendingPromotionCompilationJob } from "../../utils/promotions/promotionUtils";

const usePromotionsApi = () => {
  const axios = useContext(ApiContext);
  const {
    PROMOTION_SERVICE_URL: baseUrl,
    ENABLE_HIDING_FUTURE_DATED_PROMOTIONS_IN_ORDER_CREATION,
    ENABLE_ASYNC_DOWNLOAD_WITH_SCALES,
  } = useContext(ConfigContext);
  return {
    getPromotionsList: async (
      accountId: string,
      lang: string,
      partNumber?: string
    ): Promise<IPromotion[]> => {
      let queryParameters = `accountId=${accountId}&lang=${lang}`;
      if (partNumber) queryParameters += `&partNumber=${partNumber}`;
      queryParameters += ENABLE_HIDING_FUTURE_DATED_PROMOTIONS_IN_ORDER_CREATION
        ? `&enableFutureTpm=true`
        : "";
      const response = await axios.getWithRetries<Promotions>(
        `${baseUrl}/tradepromotions/v1/search?${queryParameters}`
      );
      return response.data.promotions;
    },

    getPromotions: async (
      accountIds: string[],
      language: string = "en_US",
      promotionType?: PromotionType
    ): Promise<IPromotion[]> => {
      const url = `${baseUrl}/tradepromotions/v2/search${
        ENABLE_HIDING_FUTURE_DATED_PROMOTIONS_IN_ORDER_CREATION
          ? "?enableFutureTpm=true"
          : ""
      }`;
      const response = await axios.postWithRetries<Promotions>(url, {
        accountIds,
        language,
        promotionType,
      });
      return response.data.promotions;
    },

    getAttachments: (promotionId: string): Promise<Attachment[]> =>
      axios
        .getWithRetries<Attachments>(
          `${baseUrl}/tradepromotions/v1/${promotionId}/attachments`
        )
        .then((response) => response.data.attachments),

    getPromotionDetails: (
      accountId: string,
      promotionId: string,
      language: string
    ): Promise<PromotionsDetails> => {
      const url = `${baseUrl}/tradepromotions/v1/${promotionId}?accountId=${accountId}&lang=${language}${
        ENABLE_HIDING_FUTURE_DATED_PROMOTIONS_IN_ORDER_CREATION
          ? "&enableFutureTpm=true"
          : ""
      }`;
      return axios
        .getWithRetries<PromotionsDetails>(url)
        .then((response) => response.data);
    },

    getPartsMapping: (
      promotionId: string,
      params: string[]
    ): Promise<PartsMappingResponse> =>
      axios
        .getWithRetries<PartsMappingResponse>(
          `${baseUrl}/tradepromotions/v1/${promotionId}/part-mapping?${params.join(
            "&"
          )}`
        )
        .then((response) => response.data),
    getPartsMappingV2: ({
      promotionId,
      queryParams,
    }: PartMappingRequest): Promise<PartsMappingResponseV2> => {
      type PartsMappingQueryParams = PartMappingRequest["queryParams"];
      const query = Object.keys(queryParams)
        .filter(
          (key) =>
            queryParams[key as keyof PartsMappingQueryParams] !== undefined &&
            queryParams[key as keyof PartsMappingQueryParams] !== ""
        )
        .map(
          (key) => `${key}=${queryParams[key as keyof PartsMappingQueryParams]}`
        )
        .join("&");

      return axios
        .getWithRetries<PartsMappingResponseV2>(
          `${baseUrl}/tradepromotions/v1/${promotionId}/part-mapping?${query}`
        )
        .then((response) => response.data);
    },
    getPartsMappingWithFilters: ({
      promotionId,
      queryParams,
    }: PartMappingRequest): Promise<PartsMappingResponse> => {
      type PartsMappingQueryParams = PartMappingRequest["queryParams"];
      const query = Object.keys(queryParams)
        .filter(
          (key) =>
            queryParams[key as keyof PartsMappingQueryParams] !== undefined &&
            queryParams[key as keyof PartsMappingQueryParams] !== ""
        )
        .map(
          (key) => `${key}=${queryParams[key as keyof PartsMappingQueryParams]}`
        )
        .join("&");

      return axios
        .getWithRetries<PartsMappingResponse>(
          `${baseUrl}/tradepromotions/v1/${promotionId}/part-mapping?${query}`
        )
        .then((response) => response.data);
    },

    downloadPromotionsListExcel: (
      accountId: string,
      lang: string
    ): Promise<AxiosResponse<BlobResponse>> => {
      const url = `${baseUrl}/tradepromotions/v1/all-promotions/excel/download?accountId=${accountId}&lang=${lang}${
        ENABLE_HIDING_FUTURE_DATED_PROMOTIONS_IN_ORDER_CREATION
          ? "&enableFutureTpm=true"
          : ""
      }`;
      return axios.getWithRetries<BlobResponse>(url, {
        responseType: "blob",
      });
    },
    downloadMultipleAccountsPromotionsExcel: (
      accountIds: string[],
      language: string
    ): Promise<AxiosResponse<BlobResponse>> => {
      const url = `${baseUrl}/tradepromotions/v1/all-promotions/excel/download`;
      return axios.postWithRetries<BlobResponse>(
        url,
        {
          accountIds,
          language,
          enableFutureTpm:
            ENABLE_HIDING_FUTURE_DATED_PROMOTIONS_IN_ORDER_CREATION ?? false,
        },
        {
          responseType: "blob",
        }
      );
    },

    downloadPartsMappingExcel: (
      promotionId: string,
      accountId: string,
      language: string,
      totalParts: number,
      hideDiscountAndCriteriaColumns?: boolean,
      showLineLevelScaleColumns?: boolean,
      locale?: string
    ): Promise<AxiosResponse<BlobResponse>> => {
      const params = new URLSearchParams({
        accountId,
        lang: language,
        totalParts: String(totalParts),
      });
      if (hideDiscountAndCriteriaColumns) {
        params.append("HideColumns", "true");
      }
      if (showLineLevelScaleColumns) {
        params.append("ShowLineLevelScaleColumns", "true");
      }
      if (locale) {
        params.append("locale", locale);
      }
      return axios.getWithRetries<BlobResponse>(
        `${baseUrl}/tradepromotions/v1/${promotionId}/excel/download?${params.toString()}`,
        {
          responseType: "blob",
        }
      );
    },

    invokeDownloadExcel: (
      promotionId: string,
      accountId: string,
      language: string,
      scalesExist?: boolean,
      hideDiscountAndCriteriaColumns?: boolean,
      showLineLevelScaleColumns?: boolean
    ): Promise<AxiosResponse<PendingPromotionCompilationJob>> => {
      const params = new URLSearchParams({
        accountId,
        lang: language,
      });

      if (ENABLE_ASYNC_DOWNLOAD_WITH_SCALES) {
        const scalesExistValue = scalesExist ?? false;
        params.append("scalesExist", String(scalesExistValue));
      }
      const effectiveHideDiscountAndCriteriaColumns = Boolean(
        hideDiscountAndCriteriaColumns
      );
      const effectiveShowLineLevelScaleColumns =
        effectiveHideDiscountAndCriteriaColumns
          ? false
          : Boolean(showLineLevelScaleColumns);

      params.append(
        "HideColumns",
        String(effectiveHideDiscountAndCriteriaColumns)
      );
      params.append(
        "ShowLineLevelScaleColumns",
        String(effectiveShowLineLevelScaleColumns)
      );
      return axios.getWithRetries<PendingPromotionCompilationJob>(
        `${baseUrl}/tradepromotions/v1/${promotionId}/invoke/download?${params.toString()}`
      );
    },

    getExcelCompilationStatuses: (
      accountId: string,
      jobIds: string[]
    ): Promise<PromotionDownloadStatusesResponse> =>
      axios
        .postWithRetries<PromotionDownloadStatusesResponse>(
          `${baseUrl}/tradepromotions/v1/excel-compilation-status?accountId=${accountId}`,
          { jobIds }
        )
        .then((res) => res.data),

    generateS3AttachmentUrl: async (jobId: string): Promise<string> =>
      axios
        .getWithRetries<string>(
          `${baseUrl}/tradepromotions/v1/promotions/${jobId}/attachment-url`
        )
        .then(({ data }) => data),

    getDiscountScales: (promotionId: string): Promise<unknown> =>
      axios
        .getWithRetries<unknown>(
          `${baseUrl}/tradepromotions/v1/${promotionId}/discount-scale`
        )
        .then((response) => response.data),
  };
};

export default usePromotionsApi;
